/**
 * Entity Theme Registry — the single source of truth for how entities, task
 * priorities, and task statuses are drawn across the Plate (and, over time,
 * the rest of the app).
 *
 * Why this exists: the same entity (deal / fault / job) was previously drawn
 * with different icons + colours in every surface (nav, mentions panel,
 * dashboard shortcuts, scheduler…). This file collapses that into one
 * canonical icon + colour token per entity kind so everything reads
 * consistently and the icons can be reused app-wide.
 *
 * Colour tokens use semantic Tailwind classes (text-*-400, bg-*-500/15) so
 * light + dark themes already work. Tailwind's JIT requires literal class
 * strings, so every variant is written out in full below — do NOT build these
 * class names dynamically.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Zap,
  AlertCircle,
  ClipboardList,
  FolderKanban,
  Package,
  Receipt,
  Users,
  Truck,
  MessageSquare,
  CheckCircle2,
  Circle,
  CircleDot,
  Ban,
  Flame,
  ArrowUp,
  Minus,
  ArrowDown,
} from 'lucide-react';
import type { TaskEntityKind, TaskPriority, TaskStatus } from '@/types/schema';

// ============================================================================
// ENTITY THEMES
// ============================================================================

export interface EntityTheme {
  /** Canonical lucide icon for this entity kind. */
  Icon: LucideIcon;
  /** Human label. */
  label: string;
  /** Foreground colour class, e.g. for a glyph on a transparent surface. */
  text: string;
  /** Tinted tile background used by <EntityIcon>. */
  tile: string;
  /** Pill/chip classes (bg + text). */
  badge: string;
  /** Ring colour class for tiles/avatars. */
  ring: string;
  /** Left accent-rail background for cards. */
  accentBar: string;
  /** Small status dot background. */
  dot: string;
}

export const ENTITY_THEMES: Record<TaskEntityKind, EntityTheme> = {
  deal: {
    Icon: Zap,
    label: 'Deal',
    text: 'text-amber-400',
    tile: 'bg-amber-500/15 text-amber-400',
    badge: 'bg-amber-500/15 text-amber-400',
    ring: 'ring-amber-400/30',
    accentBar: 'bg-amber-500',
    dot: 'bg-amber-400',
  },
  fault: {
    Icon: AlertCircle,
    label: 'Fault',
    text: 'text-rose-400',
    tile: 'bg-rose-500/15 text-rose-400',
    badge: 'bg-rose-500/15 text-rose-400',
    ring: 'ring-rose-400/30',
    accentBar: 'bg-rose-500',
    dot: 'bg-rose-400',
  },
  job: {
    Icon: ClipboardList,
    label: 'Job',
    text: 'text-sky-400',
    tile: 'bg-sky-500/15 text-sky-400',
    badge: 'bg-sky-500/15 text-sky-400',
    ring: 'ring-sky-400/30',
    accentBar: 'bg-sky-500',
    dot: 'bg-sky-400',
  },
  project: {
    Icon: FolderKanban,
    label: 'Project',
    text: 'text-blue-400',
    tile: 'bg-blue-500/15 text-blue-400',
    badge: 'bg-blue-500/15 text-blue-400',
    ring: 'ring-blue-400/30',
    accentBar: 'bg-blue-500',
    dot: 'bg-blue-400',
  },
  provisioning: {
    Icon: Package,
    label: 'Provisioning',
    text: 'text-orange-400',
    tile: 'bg-orange-500/15 text-orange-400',
    badge: 'bg-orange-500/15 text-orange-400',
    ring: 'ring-orange-400/30',
    accentBar: 'bg-orange-500',
    dot: 'bg-orange-400',
  },
  billing: {
    Icon: Receipt,
    label: 'Billing',
    text: 'text-emerald-400',
    tile: 'bg-emerald-500/15 text-emerald-400',
    badge: 'bg-emerald-500/15 text-emerald-400',
    ring: 'ring-emerald-400/30',
    accentBar: 'bg-emerald-500',
    dot: 'bg-emerald-400',
  },
  customer: {
    Icon: Users,
    label: 'Customer',
    text: 'text-violet-400',
    tile: 'bg-violet-500/15 text-violet-400',
    badge: 'bg-violet-500/15 text-violet-400',
    ring: 'ring-violet-400/30',
    accentBar: 'bg-violet-500',
    dot: 'bg-violet-400',
  },
  vehicle: {
    Icon: Truck,
    label: 'Vehicle',
    text: 'text-cyan-400',
    tile: 'bg-cyan-500/15 text-cyan-400',
    badge: 'bg-cyan-500/15 text-cyan-400',
    ring: 'ring-cyan-400/30',
    accentBar: 'bg-cyan-500',
    dot: 'bg-cyan-400',
  },
  service_request: {
    Icon: MessageSquare,
    label: 'Service Request',
    text: 'text-teal-400',
    tile: 'bg-teal-500/15 text-teal-400',
    badge: 'bg-teal-500/15 text-teal-400',
    ring: 'ring-teal-400/30',
    accentBar: 'bg-teal-500',
    dot: 'bg-teal-400',
  },
  general: {
    Icon: CheckCircle2,
    label: 'Task',
    text: 'text-slate-400',
    tile: 'bg-slate-500/15 text-slate-400',
    badge: 'bg-slate-500/15 text-slate-400',
    ring: 'ring-slate-400/30',
    accentBar: 'bg-slate-500',
    dot: 'bg-slate-400',
  },
};

export function getEntityTheme(kind?: TaskEntityKind | null): EntityTheme {
  return ENTITY_THEMES[(kind as TaskEntityKind) ?? 'general'] ?? ENTITY_THEMES.general;
}

/**
 * Map the legacy `TodoItem.sourceType` ('deal' | 'fault' | 'project') forward
 * into the richer `TaskEntityKind`. Used by `normalizeTask()`.
 */
export function sourceTypeToEntityKind(
  sourceType?: 'deal' | 'fault' | 'project' | null,
): TaskEntityKind {
  if (sourceType === 'deal') return 'deal';
  if (sourceType === 'fault') return 'fault';
  if (sourceType === 'project') return 'project';
  return 'general';
}

// ============================================================================
// PRIORITY THEME (reuses the fault-severity colour model)
// ============================================================================

export interface PriorityTheme {
  Icon: LucideIcon;
  label: string;
  text: string;
  badge: string;
  ring: string;
  accentBar: string;
  dot: string;
  /** Lower = more important — for sorting. */
  rank: number;
}

export const PRIORITY_THEME: Record<TaskPriority, PriorityTheme> = {
  urgent: {
    Icon: Flame,
    label: 'Urgent',
    text: 'text-red-400',
    badge: 'bg-red-500/15 text-red-400',
    ring: 'ring-red-400/30',
    accentBar: 'bg-red-500',
    dot: 'bg-red-500',
    rank: 0,
  },
  high: {
    Icon: ArrowUp,
    label: 'High',
    text: 'text-orange-400',
    badge: 'bg-orange-500/15 text-orange-400',
    ring: 'ring-orange-400/30',
    accentBar: 'bg-orange-500',
    dot: 'bg-orange-400',
    rank: 1,
  },
  // Normal is deliberately QUIET (slate, not the fault-model's yellow) — it's
  // the default, and a plate full of ordinary tasks shouldn't scream caution.
  // Only urgent / high / low carry colour.
  normal: {
    Icon: Minus,
    label: 'Normal',
    text: 'text-slate-400',
    badge: 'bg-slate-500/15 text-slate-400',
    ring: 'ring-slate-400/30',
    accentBar: 'bg-slate-500/50',
    dot: 'bg-slate-400',
    rank: 2,
  },
  low: {
    Icon: ArrowDown,
    label: 'Low',
    text: 'text-green-400',
    badge: 'bg-green-500/15 text-green-400',
    ring: 'ring-green-400/30',
    accentBar: 'bg-green-500',
    dot: 'bg-green-400',
    rank: 3,
  },
};

export const PRIORITY_ORDER: TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

export function getPriorityTheme(priority?: TaskPriority | null): PriorityTheme {
  return PRIORITY_THEME[(priority as TaskPriority) ?? 'normal'] ?? PRIORITY_THEME.normal;
}

// ============================================================================
// STATUS THEME (kanban columns)
// ============================================================================

export interface StatusTheme {
  Icon: LucideIcon;
  label: string;
  text: string;
  badge: string;
  dot: string;
  /** Column header accent bar. */
  accentBar: string;
  /** Left order in the board. */
  order: number;
}

export const STATUS_THEME: Record<TaskStatus, StatusTheme> = {
  todo: {
    Icon: Circle,
    label: 'To Do',
    text: 'text-slate-400',
    badge: 'bg-slate-500/15 text-slate-400',
    dot: 'bg-slate-400',
    accentBar: 'bg-slate-400',
    order: 0,
  },
  in_progress: {
    Icon: CircleDot,
    label: 'In Progress',
    text: 'text-blue-400',
    badge: 'bg-blue-500/15 text-blue-400',
    dot: 'bg-blue-400',
    accentBar: 'bg-blue-500',
    order: 1,
  },
  blocked: {
    Icon: Ban,
    label: 'Blocked',
    text: 'text-rose-400',
    badge: 'bg-rose-500/15 text-rose-400',
    dot: 'bg-rose-500',
    accentBar: 'bg-rose-500',
    order: 2,
  },
  done: {
    Icon: CheckCircle2,
    label: 'Done',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/15 text-emerald-400',
    dot: 'bg-emerald-400',
    accentBar: 'bg-emerald-500',
    order: 3,
  },
};

export const STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];

export function getStatusTheme(status?: TaskStatus | null): StatusTheme {
  return STATUS_THEME[(status as TaskStatus) ?? 'todo'] ?? STATUS_THEME.todo;
}
